import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { DropdownButton } from 'components/common/dropdown';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { Button } from '.';

import type { JSX } from 'react';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  actionCategory?: ButtonCategory;
  actionText: string;
  buttonCategory?: ButtonCategory;
  buttonIcon?: IconProp;
  buttonIconStyle?: CSSProperties;
  buttonIsDropdown?: boolean;
  buttonIsRounded?: boolean;
  buttonSize?: ButtonSize;
  buttonStyle?: CSSProperties;
  buttonText?: string | JSX.Element;
  buttonTextHideBreakpoint?: number;
  buttonTooltip?: string;
  category?: ButtonCategory;
  confirmText?: ReactNode;
  confirmTitle?: string;
  disabled?: boolean;
  modalWidth?: React.ComponentProps<typeof Modal>['width'];
  onConfirm: () => Promise<boolean> | boolean | undefined;
  stopPropagation?: boolean;
  successMessage?: string;
  validateBeforeClick?: () => boolean;
}

const ConfirmButton = ({
  actionCategory,
  actionText,
  buttonCategory,
  buttonIcon,
  buttonIconStyle,
  buttonIsDropdown,
  buttonIsRounded,
  buttonSize,
  buttonStyle,
  buttonText,
  buttonTextHideBreakpoint,
  buttonTooltip,
  category,
  confirmText,
  confirmTitle,
  disabled,
  modalWidth,
  onConfirm,
  stopPropagation,
  successMessage,
  validateBeforeClick
}: Props) => {
  const isMounted = useRef<boolean>(undefined);

  const { dispatch } = useDispatch();
  const { messages } = useReduxState();

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const hideModal = (ev) => {
    if (ev && stopPropagation) ev.stopPropagation();
    setModalVisible(false);
  };

  const showModal = (ev) => {
    if (ev && stopPropagation) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    setModalVisible(true);
  };

  const handleClick = (ev) => {
    if (validateBeforeClick) {
      const isValid = validateBeforeClick();
      if (!isValid) return;
    }

    showModal(ev);
  };

  const handleConfirm = async (ev) => {
    try {
      if (stopPropagation) ev.stopPropagation();

      setLoading(true);

      const success = await onConfirm();

      if (isMounted.current) {
        setLoading(false);
        setModalVisible(!success);
      }

      if (success && successMessage) {
        dispatch(actions.addNotice({ text: successMessage, type: 'success' }));
      } else if (!success) return dispatch.handleErrorDispatch(null);
    } catch (err) {
      setLoading(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const tooltipProps = buttonTooltip
    ? { 'aria-label': buttonTooltip, 'data-tooltip': buttonTooltip }
    : {};

  return (
    <>
      {buttonIsDropdown ? (
        <DropdownButton
          disabled={disabled}
          icon={buttonIcon}
          onClick={handleClick}
          style={buttonStyle}
          text={buttonText}
          {...tooltipProps}
        />
      ) : (
        <Button
          category={buttonCategory || category}
          disabled={disabled}
          icon={buttonIcon}
          iconStyle={buttonIconStyle}
          isRounded={buttonIsRounded}
          onClick={handleClick}
          size={buttonSize}
          style={buttonStyle}
          text={buttonText}
          textHideBreakpoint={buttonTextHideBreakpoint}
          {...tooltipProps}
        />
      )}
      <ModalPortal>
        <Modal
          onHide={hideModal}
          stopPropagation={stopPropagation}
          title={confirmTitle}
          visible={modalVisible}
          width={modalWidth}
        >
          <ModalContent>{confirmText}</ModalContent>
          <ModalFooter>
            <Button
              category="light"
              onClick={hideModal}
              text={messages.commonCancel}
            />
            <Button
              category={actionCategory || category}
              loading={loading}
              onClick={handleConfirm}
              text={actionText}
            />
          </ModalFooter>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default ConfirmButton;
